import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  styled,
  CircularProgress,
} from "@mui/material";
import {
  CancelFormActionButton,
  PrimaryFormLoadingActionButton,
} from "../../Features/Table/GenericDraggableTable/TableStyles";
import { manageIotTesting } from "../../Api/IoTApi";

const StyledDialog = styled(Dialog)(() => ({
  "& .MuiPaper-root": {
    borderRadius: 8,
    width: "480px",
    maxWidth: "95%",
  },
}));

const FieldRow = ({ label, value }) => (
  <Box sx={{ display: "flex", mb: 1.5 }}>
    <Typography sx={{ width: 140, color: "#6B7280", fontSize: 13, fontWeight: 500, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 13, color: "#111827", fontWeight: 400 }}>
      {value || "—"}
    </Typography>
  </Box>
);

const ResultBadge = ({ resultType }) => {
  const isPassed = resultType === "passed";
  return (
    <Box
      sx={{
        display: "inline-block",
        px: 1.5,
        py: 0.25,
        borderRadius: "12px",
        backgroundColor: isPassed ? "#D1FAE5" : "#FEE2E2",
        color: isPassed ? "#065F46" : "#991B1B",
        fontSize: 12,
        fontWeight: 600,
        mb: 2,
      }}
    >
      Test Result: {isPassed ? "Passed" : "Failed"}
    </Box>
  );
};

const StatusAfterBadge = () => (
  <Box
    sx={{
      display: "inline-block",
      px: 1.5,
      py: 0.25,
      borderRadius: "12px",
      backgroundColor: "#F3F4F6",
      color: "#374151",
      fontSize: 12,
      fontWeight: 500,
    }}
  >
    Unmapped
  </Box>
);

const formatDateTime = (isoString) => {
  if (!isoString) return "—";
  try {
    return new Date(isoString).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return isoString;
  }
};

const EndTestingModal = ({ open, device, resultType, onClose, onSuccess }) => {
  const [remarks, setRemarks] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [remarksError, setRemarksError] = useState(null);

  useEffect(() => {
    if (!open) {
      setRemarks("");
      setError(null);
      setRemarksError(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!remarks.trim()) {
      setRemarksError("Remarks are required");
      return;
    }
    setIsLoading(true);
    setError(null);
    const res = await manageIotTesting(device.imei, {
      action: "end",
      result: resultType,
      remarks: remarks.trim(),
    });
    setIsLoading(false);

    if (res?.responseStatus?.code === 200) {
      onSuccess(res.response?.data);
      handleClose();
    } else {
      setError(res?.responseStatus?.msg || "Something went wrong.");
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    setRemarks("");
    setError(null);
    setRemarksError(null);
    onClose();
  };

  if (!device) return null;

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogTitle sx={{ pb: 1, fontSize: 15, fontWeight: 600 }}>
        {resultType === "passed" ? "Mark as Passed" : "Mark as Failed"}
      </DialogTitle>
      <DialogContent sx={{ pt: "8px !important" }}>
        <ResultBadge resultType={resultType} />
        <FieldRow label="IMEI" value={device.imei} />
        <FieldRow label="MSISDN" value={device.msisdn} />
        <FieldRow label="Testing started" value={formatDateTime(device.testingStartedAt)} />
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography sx={{ width: 140, color: "#6B7280", fontSize: 13, fontWeight: 500, flexShrink: 0 }}>
            Status after
          </Typography>
          <StatusAfterBadge />
        </Box>
        <TextField
          label="Remarks *"
          multiline
          rows={3}
          fullWidth
          value={remarks}
          onChange={(e) => { setRemarks(e.target.value); setRemarksError(null); }}
          error={!!error || !!remarksError}
          helperText={remarksError || undefined}
          placeholder="Enter remarks"
          inputProps={{ maxLength: 500 }}
          sx={{
            "& .MuiInputLabel-root": { fontSize: 13 },
            "& .MuiInputBase-input": { fontSize: 14 },
          }}
        />
        {error && (
          <Typography sx={{ mt: 1, color: "#DC2626", fontSize: 13 }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <CancelFormActionButton onClick={handleClose} disabled={isLoading}>
          Cancel
        </CancelFormActionButton>
        <PrimaryFormLoadingActionButton
          onClick={handleConfirm}
          disabled={isLoading}
          sx={
            resultType === "failed"
              ? { backgroundColor: "#DC2626", "&:hover": { backgroundColor: "#B91C1C" } }
              : {}
          }
        >
          {isLoading ? <CircularProgress size={16} color="inherit" /> : "Confirm"}
        </PrimaryFormLoadingActionButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default EndTestingModal;
